import { confirmDialog, promptDialog } from "@fg/ui-theme/dialogs";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, Plug, Plus, Trash2, X } from "@fg/icons";
import { connectParityApi, type ChannelTabSummary, type WebhookSummary } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useConnectPolicy } from "@/api/hooks";
import { cn } from "@/lib/utils";

/** What a channel shows: its posts, its Files, or one pinned URL tab. */
export type ChannelActiveTab =
    | { kind: "posts" }
    | { kind: "files" }
    | { kind: "tab"; tab: ChannelTabSummary };
type ActiveTab = ChannelActiveTab;

/**
 * Teams-style tab strip pinned to the channel header. Always-on
 * "Posts" tab + N user-pinned URL tabs. Clicking a user tab swaps
 * the channel content area for an iframe of the configured URL.
 * Plus icon opens the add-tab form; gear opens incoming-webhook
 * management.
 */
export function ChannelTabs({
    channelId,
    active,
    onChange,
    onOpenWebhooks
}: {
    channelId: string;
    active: ActiveTab;
    onChange: (next: ActiveTab) => void;
    onOpenWebhooks: () => void;
}) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const policy = useConnectPolicy();
    const allowTabs = policy.data?.allowChannelTabs ?? true;
    const allowWebhooks = policy.data?.allowIncomingWebhooks ?? true;
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState("");
    const [url, setUrl] = useState("");
    const [error, setError] = useState<string | null>(null);

    const tabs = useQuery({
        queryKey: ["channel-tabs", channelId, token],
        queryFn: () => connectParityApi.listChannelTabs(channelId, token),
        enabled: Boolean(token)
    });

    const addTab = useMutation({
        mutationFn: () => connectParityApi.addChannelTab(channelId, name.trim(), url.trim(), token),
        onSuccess: () => {
            setAdding(false);
            setName("");
            setUrl("");
            void qc.invalidateQueries({ queryKey: ["channel-tabs", channelId] });
        },
        onError: (e) => setError(e instanceof Error ? e.message : String(e))
    });

    const deleteTab = useMutation({
        mutationFn: (tabId: string) => connectParityApi.deleteChannelTab(channelId, tabId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["channel-tabs", channelId] })
    });

    return (
        <div className="flex items-center gap-1 border-b bg-background px-3 py-1.5">
            <TabButton
                active={active.kind === "posts"}
                onClick={() => onChange({ kind: "posts" })}
            >
                Posts
            </TabButton>
            <TabButton
                active={active.kind === "files"}
                onClick={() => onChange({ kind: "files" })}
            >
                Files
            </TabButton>
            {(tabs.data ?? []).map((t) => (
                <TabButton
                    key={t.id}
                    active={active.kind === "tab" && active.tab.id === t.id}
                    onClick={() => onChange({ kind: "tab", tab: t })}
                    onDelete={() => {
                        void confirmDialog({ title: `Remove tab "${t.name}"?`, confirmLabel: "Remove", danger: true }).then((ok) => { if (ok) deleteTab.mutate(t.id); });
                    }}
                >
                    {t.name}
                </TabButton>
            ))}
            {allowTabs ? (
                <button
                    type="button"
                    onClick={() => { setAdding(true); setError(null); }}
                    aria-label="Add tab"
                    title="Add tab"
                    className="rounded-md p-1 hover:bg-accent"
                >
                    <Plus className="h-3.5 w-3.5" />
                </button>
            ) : null}
            <div className="ml-auto" />
            {allowWebhooks ? (
                <button
                    type="button"
                    onClick={onOpenWebhooks}
                    aria-label="Manage webhooks"
                    title="Manage incoming webhooks"
                    className="flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs hover:bg-accent"
                >
                    <Plug className="h-3.5 w-3.5" />
                    Webhooks
                </button>
            ) : null}

            {adding ? (
                <div
                    role="dialog"
                    aria-label="Add tab"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget && !addTab.isPending) setAdding(false); }}
                >
                    <div className="w-full max-w-md rounded-lg border bg-card p-5 shadow-2xl">
                        <div className="mb-2 flex items-center justify-between">
                            <h2 className="text-base font-semibold">Add tab</h2>
                            <button
                                type="button"
                                onClick={() => setAdding(false)}
                                disabled={addTab.isPending}
                                aria-label="Close"
                                className="rounded p-1 hover:bg-accent disabled:opacity-50"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="mb-3 text-xs text-muted-foreground">
                            Pin any URL — dashboards, planners, wikis. Renders as an iframe inside the channel.
                        </p>
                        <label className="mb-1 block text-xs font-medium">Name</label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Status board"
                            className="mb-3 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <label className="mb-1 block text-xs font-medium">URL</label>
                        <input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://…"
                            className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        {error ? (
                            <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                                {error}
                            </p>
                        ) : null}
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setAdding(false)}
                                disabled={addTab.isPending}
                                className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => { setError(null); addTab.mutate(); }}
                                disabled={addTab.isPending || !name.trim() || !url.trim()}
                                className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                                {addTab.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function TabButton({
    active,
    onClick,
    onDelete,
    children
}: {
    active: boolean;
    onClick: () => void;
    onDelete?: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="group flex items-center">
            <button
                type="button"
                onClick={onClick}
                className={cn(
                    "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                    active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
            >
                {children}
            </button>
            {onDelete ? (
                <button
                    type="button"
                    onClick={onDelete}
                    aria-label="Remove tab"
                    title="Remove tab"
                    className="ml-0.5 hidden rounded p-0.5 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground group-hover:inline-flex"
                >
                    <Trash2 className="h-3 w-3" />
                </button>
            ) : null}
        </div>
    );
}

/**
 * Per-channel iframe pane shown when a custom tab is the active one.
 * Sandbox limits the embed so a hostile URL can't read storage / top
 * frame; host can relax with sandbox-allow-* if a partner integration
 * needs it.
 */
export function ChannelTabFrame({ tab }: { tab: ChannelTabSummary }) {
    return (
        <iframe
            title={tab.name}
            src={tab.url}
            className="h-full w-full flex-1 border-0"
            sandbox="allow-scripts allow-forms allow-popups allow-same-origin"
        />
    );
}

/**
 * Manage incoming webhooks for a channel — Teams' "Connectors". Each
 * row exposes the secret POST URL with a copy button so users can
 * paste it into an external service. Revoke flips IsRevoked so the
 * audit trail stays intact; Delete removes the row outright.
 */
export function WebhooksDialog({
    channelId,
    onClose
}: {
    channelId: string;
    onClose: () => void;
}) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const [name, setName] = useState("");
    const [error, setError] = useState<string | null>(null);

    const list = useQuery({
        queryKey: ["webhooks", channelId, token],
        queryFn: () => connectParityApi.listWebhooks(channelId, token),
        enabled: Boolean(token)
    });

    const create = useMutation({
        mutationFn: () => connectParityApi.createWebhook(channelId, name.trim() || "Webhook", token),
        onSuccess: () => {
            setName("");
            void qc.invalidateQueries({ queryKey: ["webhooks", channelId] });
        },
        onError: (e) => setError(e instanceof Error ? e.message : String(e))
    });

    const revoke = useMutation({
        mutationFn: (id: string) => connectParityApi.revokeWebhook(channelId, id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["webhooks", channelId] })
    });

    const remove = useMutation({
        mutationFn: (id: string) => connectParityApi.deleteWebhook(channelId, id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["webhooks", channelId] })
    });

    return (
        <div
            role="dialog"
            aria-label="Incoming webhooks"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="flex w-full max-w-xl flex-col rounded-lg border bg-card shadow-2xl">
                <header className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-base font-semibold">Incoming webhooks</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-accent">
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="border-b p-4">
                    <p className="mb-2 text-xs text-muted-foreground">
                        External services can POST JSON like
                        <code className="mx-1 rounded bg-muted px-1 py-0.5">{"{ \"text\": \"…\" }"}</code>
                        to a webhook URL and the message lands in this channel.
                    </p>
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium">Name</label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="GitHub releases"
                                className="w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={() => { setError(null); create.mutate(); }}
                            disabled={create.isPending}
                            className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                            Add
                        </button>
                    </div>
                    {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
                </div>

                <ul role="list" className="max-h-80 overflow-auto divide-y">
                    {list.isPending ? (
                        <li className="p-4 text-xs text-muted-foreground">Loading…</li>
                    ) : !list.data || list.data.length === 0 ? (
                        <li className="p-4 text-xs text-muted-foreground">No webhooks yet.</li>
                    ) : (
                        list.data.map((w) => (
                            <WebhookRow key={w.id} hook={w} onRevoke={() => revoke.mutate(w.id)} onDelete={() => remove.mutate(w.id)} />
                        ))
                    )}
                </ul>
            </div>
        </div>
    );
}

function WebhookRow({
    hook,
    onRevoke,
    onDelete
}: {
    hook: WebhookSummary;
    onRevoke: () => void;
    onDelete: () => void;
}) {
    const [copied, setCopied] = useState(false);
    const ingestUrl = connectParityApi.webhookIngestUrl(hook.token);

    useEffect(() => {
        if (!copied) return;
        const id = window.setTimeout(() => setCopied(false), 1500);
        return () => window.clearTimeout(id);
    }, [copied]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(ingestUrl);
            setCopied(true);
        } catch {
            void promptDialog({ title: "Copy webhook URL", value: ingestUrl, confirmLabel: "Close" });
        }
    };

    return (
        <li className="flex items-center gap-3 px-4 py-3 text-xs">
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{hook.name}</span>
                    {hook.isRevoked ? (
                        <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-destructive">
                            Revoked
                        </span>
                    ) : null}
                </div>
                <div className="truncate text-[10px] text-muted-foreground">
                    {ingestUrl}
                </div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                    {hook.callCount} call{hook.callCount === 1 ? "" : "s"}
                    {hook.lastUsedAt ? ` · last ${new Date(hook.lastUsedAt).toLocaleString()}` : null}
                </div>
            </div>
            <button
                type="button"
                onClick={() => void copy()}
                className="rounded-md border px-2 py-1 hover:bg-accent"
                title="Copy webhook URL"
            >
                <Copy className="h-3 w-3" /> {copied ? "Copied" : "Copy"}
            </button>
            {!hook.isRevoked ? (
                <button type="button" onClick={onRevoke} className="rounded-md border px-2 py-1 hover:bg-accent">
                    Revoke
                </button>
            ) : null}
            <button type="button" onClick={onDelete} className="rounded-md border px-2 py-1 hover:bg-accent">
                <Trash2 className="h-3 w-3" />
            </button>
        </li>
    );
}
